import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, CheckCircle2, Circle, Star, MoreHorizontal, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { playTaskComplete, playPop, playSoundIfEnabled } from '../utils/sounds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANES = [
  {
    key: 'today',
    title: 'Det här vill jag få gjort',
    bold: 'idag',
    bg: 'bg-green-50',
    border: 'border-l-[5px] border-l-green-400',
    btnBg: 'bg-green-200/70 hover:bg-green-300/70',
    btnText: 'text-green-900',
    points: 15,
    dotEmoji: '🟢',
  },
  {
    key: 'week',
    title: 'Det här vill jag få gjort',
    bold: 'den här veckan',
    bg: 'bg-amber-50',
    border: 'border-l-[5px] border-l-amber-400',
    btnBg: 'bg-amber-200/70 hover:bg-amber-300/70',
    btnText: 'text-amber-900',
    points: 10,
    dotEmoji: '🟡',
  },
  {
    key: 'later',
    title: 'Fina idéer som',
    bold: 'kan vänta',
    bg: 'bg-rose-50',
    border: 'border-l-[5px] border-l-rose-300',
    btnBg: 'bg-rose-200/70 hover:bg-rose-300/70',
    btnText: 'text-rose-900',
    points: 5,
    dotEmoji: '🔴',
  },
];

export default function TaskList({ onTaskComplete }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingLane, setAddingLane] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);

  const persistOrder = useCallback(async (currentTasks) => {
    const order = currentTasks.map((t) => t.id);
    try {
      await axios.post(`${API}/tasks/reorder`, { order }, { withCredentials: true });
    } catch { /* ignore */ }
  }, []);

  const onDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch { /* ignore */ }
  };
  const onDragOver = (e, id, laneKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!draggingId || draggingId === id) return;
    if (overId === id) return;
    setOverId(id);
    setTasks((arr) => {
      const from = arr.findIndex((t) => t.id === draggingId);
      const to = arr.findIndex((t) => t.id === id);
      if (from === -1 || to === -1 || from === to) return arr;
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      if (laneKey && moved.lane !== laneKey) moved.lane = laneKey;
      next.splice(to, 0, moved);
      return next;
    });
  };
  const onDragEnd = () => {
    const was = draggingId;
    setDraggingId(null);
    setOverId(null);
    if (!was) return;
    persistOrder(tasks);
  };

  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tasks`, { withCredentials: true });
      setTasks(data);
    } catch (e) {
      console.error('Failed to fetch tasks:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleAddTask = async (lane) => {
    if (!newTaskTitle.trim()) return;
    const laneConfig = LANES.find(l => l.key === lane);
    try {
      const { data } = await axios.post(`${API}/tasks`, {
        title: newTaskTitle.trim(),
        lane,
        points: laneConfig?.points || 10,
        priority: lane === 'today' ? 'high' : lane === 'week' ? 'medium' : 'low',
      }, { withCredentials: true });
      setTasks([data, ...tasks]);
      setNewTaskTitle('');
      setAddingLane(null);
      toast.success('Task tillagd!');
      playSoundIfEnabled(playPop);
    } catch (e) {
      toast.error('Kunde inte lägga till');
    }
  };

  const handleToggleTask = async (task) => {
    try {
      const { data } = await axios.patch(
        `${API}/tasks/${task.id}`,
        { completed: !task.completed },
        { withCredentials: true }
      );
      setTasks(tasks.map(t => t.id === task.id ? data : t));
      if (!task.completed) {
        toast.success(`+${task.points}p! Din kompis blir glad 💛`);
        playSoundIfEnabled(playTaskComplete);
        if (onTaskComplete) onTaskComplete();
      }
    } catch (e) {
      toast.error('Kunde inte uppdatera');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (e) {
      toast.error('Kunde inte ta bort');
    }
  };

  const handleMoveLane = async (task, newLane) => {
    const laneConfig = LANES.find(l => l.key === newLane);
    try {
      const { data } = await axios.patch(
        `${API}/tasks/${task.id}`,
        { lane: newLane, points: laneConfig?.points || 10 },
        { withCredentials: true }
      );
      setTasks(tasks.map(t => t.id === task.id ? data : t));
      toast.success(`Flyttad till "${LANES.find(l => l.key === newLane)?.bold}"`);
    } catch (e) {
      toast.error('Kunde inte flytta');
    }
  };

  if (loading) {
    return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar tasks…</div>;
  }

  return (
    <div className="space-y-5" data-testid="task-list">
      {LANES.map((lane) => {
        const laneTasks = tasks.filter(t => (t.lane || 'today') === lane.key);
        const pendingTasks = laneTasks.filter(t => !t.completed);
        const completedTasks = laneTasks.filter(t => t.completed);

        return (
          <div
            key={lane.key}
            className={`rounded-3xl ${lane.bg} ${lane.border} p-5 transition-all`}
            data-testid={`lane-${lane.key}`}
          >
            {/* Lane Header */}
            <p className="text-lg font-['Nunito'] mb-3">
              {lane.title} <strong className="font-['Fredoka']">{lane.bold}</strong>.
            </p>

            {/* Add Task Button / Form */}
            {addingLane === lane.key ? (
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTask(lane.key)}
                  className="neo-input flex-1 !rounded-full !py-2"
                  placeholder="What needs to be done?"
                  autoFocus
                  data-testid={`lane-input-${lane.key}`}
                />
                <button
                  onClick={() => handleAddTask(lane.key)}
                  className={`neo-btn px-5 py-2 !rounded-full ${lane.btnBg} ${lane.btnText}`}
                  data-testid={`lane-save-${lane.key}`}
                >
                  Add
                </button>
                <button
                  onClick={() => { setAddingLane(null); setNewTaskTitle(''); }}
                  className="neo-btn px-3 py-2 !rounded-full bg-white"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setAddingLane(lane.key); setNewTaskTitle(''); }}
                className={`w-full py-3 rounded-full ${lane.btnBg} ${lane.btnText} font-bold font-['Fredoka'] text-lg border-2 border-black/10 transition-all hover:scale-[1.01] mb-3`}
                data-testid={`lane-add-${lane.key}`}
              >
                + Add task
              </button>
            )}

            {/* Tasks */}
            <div className="space-y-2">
              {pendingTasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  lane={lane}
                  onToggle={handleToggleTask}
                  onDelete={handleDeleteTask}
                  onMove={handleMoveLane}
                  onDragStart={onDragStart}
                  onDragOver={(e, id) => onDragOver(e, id, lane.key)}
                  onDragEnd={onDragEnd}
                  isDragging={draggingId === task.id}
                  isOver={overId === task.id && draggingId !== task.id}
                />
              ))}
              {completedTasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  lane={lane}
                  onToggle={handleToggleTask}
                  onDelete={handleDeleteTask}
                  onMove={handleMoveLane}
                />
              ))}
            </div>

            {laneTasks.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-2">Inga tasks här än 🌸</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TaskItem({ task, lane, onToggle, onDelete, onMove, onDragStart, onDragOver, onDragEnd, isDragging, isOver }) {
  const [showMenu, setShowMenu] = useState(false);
  const canDrag = !task.completed && onDragStart;

  return (
    <div
      draggable={!!canDrag}
      onDragStart={canDrag ? (e) => onDragStart(e, task.id) : undefined}
      onDragOver={canDrag ? (e) => onDragOver(e, task.id) : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      className={`flex items-center gap-3 bg-white rounded-2xl px-4 py-3 border border-black/10 transition-all ${
        task.completed ? 'opacity-50' : ''
      } ${isDragging ? 'opacity-40 scale-[0.98]' : ''} ${isOver ? 'ring-2 ring-pink-400' : ''}`}
      data-testid={`task-item-${task.id}`}
    >
      {canDrag && (
        <GripVertical className="w-4 h-4 text-gray-300 cursor-grab active:cursor-grabbing flex-shrink-0" />
      )}
      {/* Toggle */}
      <button
        onClick={() => onToggle(task)}
        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
          task.completed
            ? 'bg-green-400 border-green-500'
            : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
        data-testid={`task-toggle-${task.id}`}
      >
        {task.completed && <CheckCircle2 className="w-4 h-4 text-white" />}
      </button>

      {/* Title */}
      <span className={`flex-1 font-['Nunito'] font-semibold ${
        task.completed ? 'line-through text-gray-400' : 'text-gray-900'
      }`}>
        {task.title}
      </span>

      {/* Points */}
      <span className="text-xs font-bold text-gray-400 flex items-center gap-0.5 flex-shrink-0">
        <Star className="w-3 h-3" fill="currentColor" />
        {task.points}
      </span>

      {/* More menu */}
      <div className="relative flex-shrink-0">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-all"
          data-testid={`task-menu-${task.id}`}
        >
          <MoreHorizontal className="w-5 h-5 text-gray-500" />
        </button>

        {showMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
            <div className="absolute right-0 top-10 z-50 neo-card p-2 min-w-[160px] !rounded-xl" data-testid={`task-dropdown-${task.id}`}>
              {LANES.filter(l => l.key !== (task.lane || 'today')).map(l => (
                <button
                  key={l.key}
                  onClick={() => { onMove(task, l.key); setShowMenu(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm font-semibold flex items-center gap-2`}
                  data-testid={`move-${task.id}-${l.key}`}
                >
                  <div className={`w-3 h-3 rounded-full ${
                    l.key === 'today' ? 'bg-green-400' : l.key === 'week' ? 'bg-amber-400' : 'bg-rose-300'
                  }`} />
                  Move to "{l.bold}"
                </button>
              ))}
              <button
                onClick={() => { onDelete(task.id); setShowMenu(false); }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-50 text-sm font-semibold text-red-500 flex items-center gap-2"
                data-testid={`task-delete-${task.id}`}
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
